/**
 * A conferência da tabela de perfis do Player 1 na tela (`RN-RA-046`, `RN-RA-047`): as mesmas
 * regras e as mesmas frases de `adm-backend/app/services/conteudo/perfis.py`, para o aviso
 * aparecer enquanto o admin digita. O painel confere de novo ao salvar; esta é só a ajuda.
 */

export interface PerfilConferido {
    titulo: string;
    texto: string;
    minimo: number | null;
    maximo: number | null;
}

const somas = (a: number, b: number) => (a === b ? `a soma ${a}` : `as somas de ${a} a ${b}`);

/** O que falta organizar, ou `null` quando a tabela está pronta para gravar. */
export function conferirPerfis(perfis: PerfilConferido[], minima: number, maxima: number): string | null {
    if (perfis.length === 0) return 'Inclua pelo menos um perfil.';
    const vistos = new Set<string>();
    for (const [i, perfil] of perfis.entries()) {
        const titulo = perfil.titulo.trim();
        if (!titulo) return `Dê um título ao Perfil ${i + 1}.`;
        if (!perfil.texto.trim()) return `Escreva o texto do Perfil ${i + 1}.`;
        if (vistos.has(titulo.toLocaleLowerCase('pt-BR'))) {
            return `Cada perfil precisa de um título diferente: «${titulo}» aparece mais de uma vez.`;
        }
        vistos.add(titulo.toLocaleLowerCase('pt-BR'));
    }
    const ultimo = perfis.length;
    for (const [i, { minimo: de, maximo: ate }] of perfis.entries()) {
        const n = i + 1;
        if (de === null && n > 1) return `Preencha o 'De' do Perfil ${n}: só o primeiro perfil pode ficar sem piso.`;
        if (ate === null && n < ultimo) return `Preencha o 'Até' do Perfil ${n}: só o último perfil pode ficar sem teto.`;
        if (de !== null && ate !== null && de > ate) return `No Perfil ${n}, o 'De' (${de}) está acima do 'Até' (${ate}).`;
    }
    for (let i = 0; i + 1 < perfis.length; i++) {
        const ate = perfis[i].maximo as number;
        const de = perfis[i + 1].minimo as number;
        if (de > ate + 1) {
            return `Falta perfil para ${somas(ate + 1, de - 1)}: ajuste o 'Até' do Perfil ${i + 1} ou o 'De' do Perfil ${i + 2}.`;
        }
        if (de <= ate) return `O Perfil ${i + 1} e o Perfil ${i + 2} cobrem ao mesmo tempo ${somas(de, ate)}.`;
    }
    const piso = perfis[0].minimo;
    const teto = perfis[ultimo - 1].maximo;
    if (piso !== null && piso > minima) {
        return `Somas abaixo de ${piso} ficam sem perfil: deixe o 'De' do Perfil 1 vazio ou baixe-o para ${minima}.`;
    }
    if (teto !== null && teto < maxima) {
        return `Somas acima de ${teto} ficam sem perfil: deixe o 'Até' do Perfil ${ultimo} vazio ou suba-o para ${maxima}.`;
    }
    for (const perfil of perfis) {
        if ((perfil.minimo !== null && perfil.minimo > maxima) || (perfil.maximo !== null && perfil.maximo < minima)) {
            return `O perfil «${perfil.titulo.trim()}» fica inalcançável: as somas possíveis vão de ${minima} a ${maxima}.`;
        }
    }
    return null;
}
